import { telemetry } from "@/telemetry";
import type { DetailsBlockedReason } from "@/telemetry/events";
import { paymentMethods } from "@/telemetry/events";
import { orderReviewAmount } from "@/constants/order";
import { getApiErrorMessage } from "@/api/getApiErrorMessage";
import type { ApiError } from "@/api/hooks/types";
import type { RootStore } from ".";
import type { OrderCheckboxData, OrderPurchaseType } from "./types/order";

const getTotalBand = (total: number) => {
  if (total < 1_000) return "under_1k";
  if (total < 5_000) return "1k_to_5k";
  if (total < orderReviewAmount) return "5k_to_45k";
  return "45k_plus";
};

export class TelemetryStore {
  rootStore: RootStore;

  get chosenProducts() {
    return this.rootStore.orderProducts.filter(
      (product) => product.quantity > 0,
    );
  }

  get isOverReviewAmount() {
    return this.rootStore.orderTotals.total >= orderReviewAmount;
  }

  get detailsBlockedReason(): DetailsBlockedReason {
    if (this.chosenProducts.length === 0) return "no_products";
    if (!this.rootStore.isValidOrderMinimum) return "below_minimum";
    return "acknowledgements";
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  detailsSubmitted = () =>
    telemetry.trackEvent("details_submitted", {
      product_count: this.chosenProducts.length,
      units: this.chosenProducts.reduce(
        (units, product) => units + product.quantity,
        0,
      ),
      subtotal: this.rootStore.orderTotals.subTotal,
      email_updates_opt_in: this.rootStore.orderData.subscribeToEmailUpdates,
    });

  detailsContinueBlocked = (requiredCheckboxes: (keyof OrderCheckboxData)[]) =>
    telemetry.trackEvent("details_continue_blocked", {
      reason: this.detailsBlockedReason,
      missing_checkboxes: requiredCheckboxes
        .filter((key) => !this.rootStore.orderCheckboxData[key])
        .join(","),
    });

  paymentMethodChosen = (purchaseType: OrderPurchaseType) =>
    telemetry.trackEvent("payment_method_chosen", {
      method: paymentMethods[purchaseType],
      total_band: getTotalBand(this.rootStore.orderTotals.total),
      over_review_amount: this.isOverReviewAmount,
      po_allowed: this.rootStore.orderData.billingAllowPo,
    });

  orderCreated = (orderId: string, purchaseType: OrderPurchaseType) =>
    telemetry.trackEvent("order_created", {
      order_id: orderId,
      method: paymentMethods[purchaseType],
      total: this.rootStore.orderTotals.total,
      over_review_amount: this.isOverReviewAmount,
      product_count: this.chosenProducts.length,
      promo_applied: !!this.rootStore.orderData.promotionalCode,
    });

  orderCreateFailed = (error: ApiError, purchaseType: OrderPurchaseType) =>
    telemetry.trackEvent("order_create_failed", {
      status: error.response?.status,
      message: getApiErrorMessage(error),
      method: paymentMethods[purchaseType],
    });
}
